import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../../contexts/AuthContext";
import { AppContext } from "../../contexts/AppContext";
import { getClientWorkflowGraphs, deleteWorkflowGraph } from "../../services/workflows";
import styled from "styled-components";
import { Icon } from "@iconify/react";
import { Button, FormSelector } from "../../components/StyledElements";
import WorkflowsTable from "../../components/Tables/WorkflowsTable";
import swal from "sweetalert2";
import QueryDialog from "../../components/Dialogs/QueryDialog";
import { useAgents } from "../../contexts/AgentsContext";
import { EditWorkflow } from "./workflow/EditWorkflow";


const SmallButton = styled.div`
  height: 30px;
  width: 24px;
  background-color: #f3f4f6; 
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a0a6b1;
  cursor: pointer;

  &.selected {
    background-color: var(--button-selected-bg); 
  }
  &.selected > * {
    color: var(--button-bg);

  }
`

const VerticalSeparator = styled.div`
  width: 1px;
  height: 30px;
  background-color: #eceff4;
  margin-left: 20px;
  margin-right: 20px;
`

const WorkflowsGridWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;

`

const WorkflowCardWrapper = styled.div`
    position: relative;
    border: 1px solid var(--content-item-border-color);
    color: var(--main-font-color);
    width: 300px;
    height: 420px;
    max-height: 485px;
    background-color: var(--card-bg);
    border-radius: 5px;
    box-shadow: var(--content-item-box-shadow);
    padding: 20px 20px 0 20px;
`

const WorkflowTitle = styled.div`
    font-weight: bold;
    font-size: 20px;
    margin-top: 20px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    cursor: pointer;
`

const WorkflowSubTitle = styled.div`
    font-weight: 500;
    font-size: 16px;
    margin-top: 5px;
    color: var(--main-font-color);
    text-overflow: ellipsis;
    font-style: italic;
    white-space: nowrap;
    overflow: hidden;
    cursor: pointer;
`

const WorkflowDescription = styled.div`
    font-weight: normal;
    font-size: 16px;
    margin-top: 10px;
    font-style: italic;
    overflow: auto;
    max-height: 140px;
    height: 140px;
    margin-bottom: 10px;
`

const WorkflowData = styled.div`
    position: relative;
    width: 300px;
    margin-left: -20px;
    font-size: 14px;
    color: #a0a6b1;
    background-color: var(--card-inset-bg);
    padding: 10px 20px;
    border-top: 1px solid var(--main-bg);
    border-bottom: 1px solid var(--main-bg);
`;

const WorkflowOpenLink = styled.a`
    font-size: 15px;
    color: var(--sidebar-active-item-color);
    text-decoration: none;
    display: flex;
    flex-direction: row;
    align-items: center;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
`;

const SearchBar = styled.div`
  width: 100%;
  border-radius: 5px;
  border: 1px solid var(--content-item-border-color);  
  background-color: var(--card-bg);
  padding: 10px 20px;
  box-shadow: var(--content-item-box-shadow);
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
`

const SearchBox = styled.div`
  width: 300px;
  height: 36px;
  border-radius: 5px;
  border: 1px solid #d2d6dd;
  background-color: #f8fafb; 
  display: flex;
  align-items: center;
  padding-left: 10px;

  & * {
    color: #717886;
  }

  & input {
    height: 100%;
    border: none;
    outline: none !important;
    background: transparent;
    width: 250px;
  }

  & input:focus {
    outline:none !important;
    outline-width: 0 !important;
    box-shadow: none;
    -moz-box-shadow: none;
    -webkit-box-shadow: none;
  }
}
`

const WorkflowToolBarWrapper = styled.div`
  width: 300px;
  margin-left: -20px;
  height: 50px;
  margin-top: 0px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-evenly;
  flex-wrap: nowrap;
  padding: 0;
`

const WorkflowToolBarButton = styled.div`
  height: 100%;
  width: 100%;
  padding-top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-right: 1px solid var(--main-bg);
  cursor: pointer;

  & * {
    font-size: 20px;
 }

`;

export const Workflows = ({ show = false }) => {
  const { activeUser } = useAuthContext();
  const { setUpdatedWorkflow, updatedWorkflow } = useAgents();
  const { selectedClient } = useContext(AppContext);
  const [showQueryDialog, setShowQueryDialog] = useState(false);
  const [queryWorkflow, setQueryWorkflow] = useState(null);
  const [workflows, setWorkflows] = useState([]);
  const [filteredWorkflows, setFilteredWorkflows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("newest-first")
  const [display, setDisplay] = useState("grid");
  const [createWorkflow, setCreateWorkflow] = useState(false);
  const navigate = useNavigate();

  const sortByOptions = [
    { label: "Newest First", value: "newest-first" },
    { label: "Open Date First", value: "open-date-first" },
    { label: "Open Date Last", value: "open-date-last" },
    { label: "Start Date First", value: "start-date-first" },
    { label: "Start Date Last", value: "start-date-last" },
  ]

  useEffect(() => {
    setUpdatedWorkflow(null);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    console.log("!!! FETCHING data SETS");
    const fetchWorkflows = async () => {
      try {
        const results = await getClientWorkflowGraphs(selectedClient.id);
        if (results.success) {
          console.log("!!! FETCHED Workflows: ", results.workflows);
          setWorkflows(results.workflows);
          setFilteredWorkflows(results.workflows);
        } else {
          swal.fire({
            title: "Error fetching workflows",
            text: results.message || "An error occurred while fetching workflows. Please try again later.",
            icon: "error",
            confirmButtonText: "OK"
          });
        }
      } catch (error) {
        console.error(error);
      }
    };

    console.log("!!! SELECTED CLIENT FROM users: ", selectedClient);
    if (selectedClient) {
      fetchWorkflows();
    }
    // eslint-disable-next-line
  }, [selectedClient]);


  useEffect(() => {
    if (!searchTerm) {
      setFilteredWorkflows(workflows);
      return;
    }

    setFilteredWorkflows(workflows.filter((workflow) => {
      console.log(workflow.title, workflow.description)
      return `${workflow.name} ${workflow.description} ${workflow.chroma_collection_name}`.toLowerCase().includes(searchTerm.toLowerCase())
    }))

    // eslint-disable-next-line
  }, [searchTerm, workflows])

  const WorkflowToolBar = ({ workflow }) => {
    return (
      <WorkflowToolBarWrapper>
        {/* Toolbar buttons can be added here */}
        <WorkflowToolBarButton onClick={() => {
        }}><Icon icon="mdi:add" /></WorkflowToolBarButton>
        <WorkflowToolBarButton
          onClick={() => {
            if (activeUser?.user_type.match(/client/)) {
              navigate(`/client/workflow/${workflow.public_id}`);
            } else {
              navigate(`/workflow/${workflow.public_id}`);
            }
          }}
        ><Icon icon="mdi:edit" /></WorkflowToolBarButton>
        <WorkflowToolBarButton
          title="Query the data set with natural language and get insights, summaries, and more."
          onClick={() => {
            setQueryWorkflow(workflow);
            setShowQueryDialog(true);
          }}
        ><Icon icon="mdi:question-mark" /></WorkflowToolBarButton>

        <WorkflowToolBarButton
          onClick={() => {
            swal.fire({
              title: `Are you sure you want to delete the ${workflow.name} data set?`,
              text: "This action cannot be undone.",
              icon: "warning",
              showCancelButton: true,
              confirmButtonText: "Yes, delete it!",
              cancelButtonText: "No, keep it"
            }).then(async (result) => {
              console.log("!!! DELETE CONFIRM RESULT: ", result);
              if (result.value) {
                // Call API to delete data set here
                // If successful:
                console.log("ALL DATASETS BEFORE DELETION: ", workflows);
                console.log("!!! DELETING data SET: ", workflow);
                const updatedWorkflows = workflows.filter(ds => ds.id !== workflow.id);
                setWorkflows(updatedWorkflows);

                await deleteWorkflowGraph(workflow.public_id);
                swal.fire("Deleted!", "The data set has been deleted.", "success");
              }
            });
          }}
        ><Icon icon="mdi:trash-can" /></WorkflowToolBarButton>
      </WorkflowToolBarWrapper>
    )
  }

  const WorkflowCard = ({ workflow }) => {
    return (
      <WorkflowCardWrapper key={workflow.id}>
        <div style={{ height: "260px", minHeight: "260px" }}>
          <WorkflowTitle
            title={workflow.name}
          >{workflow.name}</WorkflowTitle>
          <WorkflowSubTitle>{workflow.chroma_collection_name}</WorkflowSubTitle>
          <WorkflowDescription>{workflow.description ?? "No description"}</WorkflowDescription>
        </div>

        <WorkflowData>
          <div>ID: {workflow.id}</div>
          <div>PUB ID: {workflow.public_id.substr(0, 8)}...</div>
          <div style={{ position: "absolute", right: "20px", top: "10px", fontSize: "12px", color: "#a0a6b1" }}>
            <WorkflowOpenLink
              onClick={() => {
                setUpdatedWorkflow(workflow);
                if (activeUser?.user_type.match(/client/)) {
                  navigate(`/client/workflow/${workflow.public_id}`);
                } else {
                  navigate(`/workflow/${workflow.public_id}`);
                }
              }}
            >View <Icon icon="mdi:arrow-right" /></WorkflowOpenLink>
          </div>
        </WorkflowData>
        <WorkflowToolBar workflow={workflow} />
      </WorkflowCardWrapper>
    )
  }

  const WorkflowsGrid = () => {
    console.log("!!! FILTERED data SETS: ", filteredWorkflows);
    return (
      <WorkflowsGridWrapper>
        { filteredWorkflows.map(workflow => {
          return <div key={workflow.id} style={{ marginRight: "24px", marginBottom: "24px" }}><WorkflowCard workflow={workflow} /></div>
        })
        }
      </WorkflowsGridWrapper>
    );
  }

  if (createWorkflow || updatedWorkflow) {
    return (
      <div
        style={{
          display: show ? "flex" : "none",
          flexDirection: "column",
          width: "100%",
        }}
      >
        <EditWorkflow
          onClose={() => {
            setCreateWorkflow(false);
            setUpdatedWorkflow(null);
          }}
        />
      </div>
    )
  }

  return (
    <div
      style={{
        display: show ? "flex" : "none",
        flexDirection: "column",
        width: "100%",
      }}
    >
      <div style={{ fontSize: "32px", fontWeight: "bold" }}>
        <div style={{ color: "var(--main-font-color)" }}>Workflows</div>
        <div style={{ fontSize: "16px", fontWeight: "italic" }}>
          ({workflows.length > filteredWorkflows.length ? `${filteredWorkflows.length} of ${workflows.length} Workflows` : `${workflows.length} Data Sets`})
        </div>
      </div>
      <div style={{ position: "absolute", right: "20px", top: "30px" }}>
        <Button
          onClick={() => {
            navigate(`/workflow/create`);
          }}
        >+ Create Workflow</Button>
      </div>

      <div style={{ marginTop: "24px", width: "100%" }}>
        <SearchBar>
          <label style={{ marginRight: "10px", color: "var(--main-font-color)" }}>Sort by: </label>
          <div style={{ width: "200px", marginRight: "20px" }}>
            <FormSelector
              placeholder="Sort By"
              value={sortBy}
              options={sortByOptions}
              onChange={(e) => {
                setSortBy(e.target.value);
              }}
            />
          </div>
          <VerticalSeparator />

          <SmallButton className={display === "list" ? "selected" : ""} style={{ marginRight: "10px" }}
            onClick={() => { setDisplay("list") }}>
            <Icon icon="material-symbols:list" />
          </SmallButton>
          <SmallButton className={display === "grid" ? "selected" : ""} style={{ marginRight: "10px" }}
            onClick={() => { setDisplay("grid") }}>
            <Icon icon="material-symbols:grid-view-outline" />
          </SmallButton>
          <div style={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
            <SearchBox>
              <Icon icon="material-symbols:search" />
              <input value={searchTerm} type="text" onChange={(e) => {
                setSearchTerm(e.target.value);
              }} />

              {searchTerm &&
                <div style={{ cursor: "pointer" }} onClick={() => setSearchTerm("")}>
                  <Icon icon="material-symbols:x-circle" />
                </div>
              }
            </SearchBox>
          </div>
        </SearchBar>
      </div>

      {
        display === "grid" &&
        <div style={{ marginTop: "24px" }}>
          <WorkflowsGrid />
        </div>
      }
      {
        display === "list" &&
        <WorkflowsTable
          hideTitle={true}
          onUpdated={(newWorkflows) => {
            setWorkflows(newWorkflows);
          }}
        />
      }
      <QueryDialog workflow={queryWorkflow} show={showQueryDialog} onClose={() => setShowQueryDialog(false)} />
    </div>
  );
};

export default Workflows;
